// features/projects/templates/professional/widgets/findings-status.js
// ═══════════════════════════════════════════════════════════
// FINDINGS STATUS — Tier-A widget
//
// Q: "What's my verdict mix right now and what does that mean?"
//
// 5-layer card: Read (verdict counts + segmented bar) · Story
// (heterogeneity narrative anchored on the strongest finding) ·
// Takeaway (the "which mechanisms transfer" reframe) · Action
// (heuristic next move) · Caveat (Phase A coverage gap).
//
// Ported from features/projects/templates/continuum/dashboard.js
// (the WhereTheResearchStandsCard). Generalizes one step toward
// domain-agnostic shape: reads from useWidgetData() instead of
// hard-fetching, so the same widget could later sit on any
// Professional project's dashboard.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const VERDICT_COLOR = {
  PARALLEL:        L.sage,
  'NEAR-PARALLEL': L.gold,
  DIVERGENT:       L.terra,
  pending:         L.dim,
};

export default function FindingsStatusWidget() {
  const { loading, comparisons } = useWidgetData();

  const buckets = useMemo(() => groupByVerdict(comparisons), [comparisons]);
  const total = comparisons.length;

  const segments = [
    { key: 'PARALLEL',      label: 'parallel',      count: buckets.PARALLEL.length,         color: VERDICT_COLOR.PARALLEL },
    { key: 'NEAR-PARALLEL', label: 'near-parallel', count: buckets['NEAR-PARALLEL'].length, color: VERDICT_COLOR['NEAR-PARALLEL'] },
    { key: 'DIVERGENT',     label: 'divergent',     count: buckets.DIVERGENT.length,        color: VERDICT_COLOR.DIVERGENT },
    { key: 'pending',       label: 'pending',       count: buckets.pending.length,          color: VERDICT_COLOR.pending },
  ];
  const visible = segments.filter(s => s.count > 0);

  const substantive = comparisons.filter(isSubstantive);
  const phaseACovered = substantive.filter(c => c.phase_a_status).length;

  if (loading) return <WidgetShell label="Findings Status"><LoadingBlock /></WidgetShell>;
  if (total === 0) {
    return (
      <WidgetShell label="Findings Status">
        <EmptyState>No findings yet. This widget surfaces verdict counts once comparisons are computed.</EmptyState>
      </WidgetShell>
    );
  }

  const story = composeStory(buckets, substantive);
  const takeaway = composeTakeaway(buckets, substantive);
  const action = composeAction(comparisons, substantive);
  const caveat = composeCaveat(substantive, phaseACovered);

  return (
    <WidgetShell label="Findings Status">
      {/* READ */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap',
        marginBottom: 10,
      }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 28, color: L.text,
          fontWeight: 300, letterSpacing: '-0.01em',
        }}>
          {total}<span style={{ fontSize: 13, color: L.dim, fontWeight: 400, marginLeft: 6 }}>findings</span>
        </div>
        <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans }}>
          {substantive.length} substantive · {phaseACovered} red-teamed
        </div>
      </div>

      {visible.length > 0 && (
        <div style={{
          display: 'flex', height: 10, borderRadius: 3, overflow: 'hidden',
          border: `1px solid ${L.b1}`, marginBottom: 8,
        }}>
          {visible.map(s => (
            <div key={s.key} title={`${s.count} ${s.label}`}
                 style={{ flex: s.count, background: s.color, opacity: 0.85 }} />
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: FONT.mono, fontSize: 10 }}>
        {visible.map(s => (
          <span key={s.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            color: s.color, letterSpacing: '0.04em',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
            {s.count} {s.label}
          </span>
        ))}
      </div>

      {/* STORY · TAKEAWAY · ACTION · CAVEAT */}
      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={L.gold}>{takeaway}</LayerSection>
      {action && (
        <LayerAction
          accent={L.sage}
          label={action.label}
          why={action.why}
        />
      )}
      {caveat && <CaveatStrip text={caveat} />}
    </WidgetShell>
  );
}

// ─── Composition helpers ──────────────────────────────────────
function composeStory(buckets, substantive) {
  if (substantive.length === 0) {
    return 'No substantive findings yet — most dynamics are still pending data on one or both sides.';
  }
  const parts = [];
  const par = buckets.PARALLEL.length;
  const near = buckets['NEAR-PARALLEL'].length;
  const div = buckets.DIVERGENT.length;
  const pend = buckets.pending.length;
  if (par > 0) parts.push(`${par} confirmed parallel${par === 1 ? '' : 's'}`);
  if (near > 0) parts.push(`${near} near-parallel${near === 1 ? '' : 's'}`);
  if (div > 0) parts.push(`${div} divergent finding${div === 1 ? '' : 's'}`);
  let body = parts.join(', ') + '. ';
  if (pend > 0) body += `${pend} ${pend === 1 ? 'dynamic remains' : 'dynamics remain'} pending. `;
  body = body[0].toUpperCase() + body.slice(1);

  const strongest = pickStrongest(substantive);
  if (strongest) {
    const dynLabel = DYN_BY_KEY[strongest.dynamic_key]?.label || strongest.dynamic_key;
    if (strongest.phase_a_status === 'survived-respecified') {
      body += `The most defensible finding (${dynLabel}) survived Phase A in respecified form.`;
    } else if (strongest.phase_a_status === 'survived') {
      body += `The most defensible finding (${dynLabel}) cleared Phase A intact.`;
    } else {
      body += `The most defensible finding (${dynLabel}) has not yet been Phase A red-teamed.`;
    }
  }
  return body.trim();
}

function composeTakeaway(buckets, substantive) {
  if (substantive.length === 0) return 'Wait for findings before drawing conclusions.';
  const par = buckets.PARALLEL.length;
  const near = buckets['NEAR-PARALLEL'].length;
  const div = buckets.DIVERGENT.length;
  if (par + near > 0 && div > 0) {
    return 'The methodology is producing a heterogeneous picture. The publishable claim is not "systems are similar" but "specific structural mechanisms produce similar distributions; others produce domain-specific differences."';
  }
  if (par + near > 0) return `All ${par + near} substantive findings show parallel or near-parallel distributions. Treat as suggestive but not conclusive until contrast emerges.`;
  if (div > 0) return `All ${div} substantive findings diverge under current operationalization. Refining operationalization may surface masked parallels.`;
  return 'Limited data; findings still consolidating.';
}

function composeAction(comps, substantive) {
  const noPhaseA = substantive.filter(c => !c.phase_a_status);
  if (noPhaseA.length > 0) {
    const target = noPhaseA.slice().sort((a, b) =>
      Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
    const dynLabel = DYN_BY_KEY[target.dynamic_key]?.label || target.dynamic_key;
    return {
      label: `Run Phase A on ${dynLabel}`,
      why: `Largest unreviewed effect (d=${(Number(target.effect_size) || 0).toFixed(2)}) — biggest leverage to convert an unverified claim into a defensible one.`,
    };
  }
  const pending = comps.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  if (pending.length > 0) {
    return {
      label: `Wire data for ${pending.length} pending dynamic${pending.length === 1 ? '' : 's'}`,
      why: 'Each wired source is a finding pulled out of indeterminacy.',
    };
  }
  return null;
}

function composeCaveat(substantive, phaseACovered) {
  if (substantive.length === 0) return null;
  if (substantive.length === phaseACovered) return null;
  const remaining = substantive.length - phaseACovered;
  return `${remaining} substantive finding${remaining === 1 ? '' : 's'} not yet Phase-A red-teamed — those claims carry elevated interpretive caution until reviewed.`;
}

function isSubstantive(c) {
  return c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase());
}
function groupByVerdict(comps) {
  const out = { PARALLEL: [], 'NEAR-PARALLEL': [], DIVERGENT: [], pending: [] };
  for (const c of comps) {
    const v = String(c.verdict || 'pending').toUpperCase();
    if (out[v]) out[v].push(c);
    else if (v === 'INSUFFICIENT_DATA' || v === 'PENDING' || !c.verdict) out.pending.push(c);
  }
  return out;
}
function pickStrongest(substantive) {
  if (substantive.length === 0) return null;
  return substantive.slice().sort((a, b) => threadScore(b) - threadScore(a))[0];
}
function threadScore(c) {
  let s = 0;
  if (c.phase_a_status === 'survived')              s += 100;
  else if (c.phase_a_status === 'survived-respecified') s += 80;
  if ((c.confidence || '').toUpperCase() === 'HIGH') s += 50;
  return s;
}

// ─── Shared shell + layer primitives ──────────────────────────
// Card pattern modeled on the wealth-building dashboard at
// /wealth-building (Compound). Uniform padding, brass title with
// bottom-divider, no per-card corner ticks (they competed with
// content at this density). Equal-row-height comes from CSS
// Grid's default align-items: stretch — no explicit height: 100%.
export function WidgetShell({ label, children }) {
  return (
    <div style={{
      padding: '18px 20px',
      background: L.s2,
      border: `1px solid ${L.b1}`,
      borderRadius: 4,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        fontFamily: FONT.mono, fontSize: 9, letterSpacing: '0.16em',
        color: L.brass, textTransform: 'uppercase', fontWeight: 600,
        borderBottom: `1px solid ${L.b1}`,
        paddingBottom: 10, marginBottom: 14,
      }}>
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}
export function LayerSection({ label, accent, children }) {
  if (children == null || children === '') return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${L.b1}` }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 8, letterSpacing: '0.14em',
        color: accent || L.dim, textTransform: 'uppercase',
        marginBottom: 5, fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 12, lineHeight: 1.55, color: L.text }}>
        {children}
      </div>
    </div>
  );
}
export function LayerAction({ accent, label, why }) {
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px',
      background: `${accent}10`,
      border: `1px solid ${accent}40`, borderRadius: 4,
    }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 9, letterSpacing: '0.06em',
        color: accent, fontWeight: 600, marginBottom: 4,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span>→</span><span>{label}</span>
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: L.mid, lineHeight: 1.5 }}>
        {why}
      </div>
    </div>
  );
}
export function CaveatStrip({ text }) {
  return (
    <div style={{
      marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${L.b1}`,
      fontFamily: FONT.sans, fontSize: 10, color: L.dim, lineHeight: 1.5,
      fontStyle: 'italic',
    }}>
      ↳ {text}
    </div>
  );
}
function LoadingBlock() {
  return (
    <div style={{
      fontSize: 12, color: L.dim, fontFamily: FONT.sans,
      fontStyle: 'italic',
    }}>
      Loading…
    </div>
  );
}
function EmptyState({ children }) {
  return (
    <div style={{
      fontSize: 12, color: L.mid, fontFamily: FONT.sans,
      lineHeight: 1.55,
    }}>
      {children}
    </div>
  );
}
